import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Clock3, ShieldCheck, ReceiptText, CalendarClock, ArrowRight, Loader2 } from 'lucide-react';
import { usePortal } from './PortalApp';
import { apiError, portalApi } from './portalApi';

const HERO = 'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const FEATURES = [
  { icon: Clock3, title: 'Live hours & T&M dollars', text: 'See who is on your site right now and what the week is costing — approved and pending.' },
  { icon: ReceiptText, title: 'Every invoice, every timesheet', text: 'Open any invoice and pull the signed weekly timesheets behind it.' },
  { icon: ShieldCheck, title: 'Audit-ready safety records', text: 'Site orientations and trade tickets for every tradesperson we send you.' },
  { icon: CalendarClock, title: 'Upcoming dispatches', text: 'Confirmed and tentative crew for your shutdowns and shift coverage.' },
];

export const LoginPane = ({ children }) => (
  <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
    <div className="hidden lg:flex p-grid-bg text-white relative overflow-hidden flex-col justify-between p-12">
      <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-luminosity" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C] via-[#0B192C]/70 to-[#0B192C]/30" />
      <div className="relative">
        <img src="/365-logo-white.png" alt="365 Industrial Solutions" className="h-14 w-auto" />
        <span className="inline-flex mt-4 p-mono text-[10px] tracking-[0.25em] font-bold text-blue-200 border border-blue-300/30 rounded px-2 py-0.5">CLIENT PORTAL</span>
      </div>
      <div className="relative max-w-lg">
        <h1 className="p-display text-4xl xl:text-5xl font-extrabold leading-[1.05]">Real-time field operations, safety compliance & cost transparency.</h1>
        <div className="mt-8 space-y-4">
          {FEATURES.map((f, i) => (
            <div key={f.title} className={`flex items-start gap-3 p-rise p-rise-${i + 1}`}>
              <span className="mt-0.5 h-9 w-9 shrink-0 rounded-lg bg-white/10 border border-white/10 inline-flex items-center justify-center"><f.icon className="w-4 h-4 text-blue-200" /></span>
              <div><div className="font-bold">{f.title}</div><div className="text-sm text-blue-100/80">{f.text}</div></div>
            </div>
          ))}
        </div>
      </div>
      <div className="relative text-xs text-blue-200/70">365 Industrial Solutions Inc. · Millwrighting · Fabrication · Rigging · Plant Maintenance</div>
    </div>
    <div className="flex items-center justify-center p-6 sm:p-10 bg-[#F8FAFC]">
      <div className="w-full max-w-md">
        <div className="lg:hidden mb-8 flex items-center gap-3">
          <img src="/365-logo-dark.png" alt="365 Industrial Solutions" className="h-10 w-auto" />
          <span className="p-mono text-[10px] tracking-[0.2em] font-bold text-slate-500 border border-slate-300 rounded px-2 py-0.5">CLIENT PORTAL</span>
        </div>
        {children}
      </div>
    </div>
  </div>
);

export default function PortalLogin() {
  const { setSession } = usePortal();
  const navigate = useNavigate();
  const loc = useLocation();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const r = await portalApi.post('/auth/login', { email, password });
      setSession(r.data);
      toast.success(`Welcome back, ${r.data.user.full_name.split(' ')[0]}`);
      navigate(loc.state?.from || '/portal', { replace: true });
    } catch (err) { setError(apiError(err, 'Sign in failed')); } finally { setBusy(false); }
  };
  const forgot = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await portalApi.post('/auth/forgot-password', { email, base_url: window.location.origin });
      setSent(true);
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <LoginPane>
      <div className="p-card p-7 sm:p-8 p-rise" data-testid="portal-login-card">
        <div className="p-overline mb-1">{mode === 'login' ? 'Sign in' : 'Reset password'}</div>
        <h2 className="p-display text-3xl font-extrabold text-slate-900">{mode === 'login' ? 'Welcome back' : 'Forgot your password?'}</h2>
        <p className="text-sm text-slate-500 mt-1">{mode === 'login' ? 'Sign in with the email 365 Industrial set up for you.' : 'Enter your email and we will send you a reset link.'}</p>

        {mode === 'login' ? (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <label className="block"><span className="p-overline">Email</span>
              <input type="email" autoComplete="username" className="p-input mt-1" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com" data-testid="portal-login-email-input" /></label>
            <label className="block"><span className="p-overline">Password</span>
              <input type="password" autoComplete="current-password" className="p-input mt-1" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="portal-login-password-input" /></label>
            {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="portal-login-error">{error}</div>}
            <button type="submit" className="p-btn p-btn-primary w-full justify-center" disabled={busy} data-testid="portal-login-submit-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Sign in to the portal
            </button>
            <div className="flex items-center justify-between text-sm pt-1">
              <button type="button" className="text-blue-700 font-semibold" onClick={() => { setMode('forgot'); setError(''); }} data-testid="portal-forgot-password-link">Forgot password?</button>
              <span className="text-slate-400">Have an invite? Use the link in your email.</span>
            </div>
          </form>
        ) : (
          <form onSubmit={forgot} className="mt-6 space-y-4">
            {sent ? (
              <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-3" data-testid="portal-forgot-sent">If <b>{email}</b> has portal access, a reset link is on its way. It is valid for 2 hours.</div>
            ) : (
              <label className="block"><span className="p-overline">Email</span>
                <input type="email" className="p-input mt-1" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com" data-testid="portal-forgot-email-input" /></label>
            )}
            {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</div>}
            {!sent && <button type="submit" className="p-btn p-btn-primary w-full justify-center" disabled={busy} data-testid="portal-forgot-submit-btn">{busy ? 'Sending…' : 'Email me a reset link'}</button>}
            <button type="button" className="text-sm text-blue-700 font-semibold" onClick={() => { setMode('login'); setSent(false); setError(''); }} data-testid="portal-back-to-login">← Back to sign in</button>
          </form>
        )}
      </div>
      <p className="text-xs text-slate-400 mt-5 text-center">Access is by invitation from 365 Industrial Solutions. Need an account for a colleague? Ask your project manager.</p>
    </LoginPane>
  );
}
